import moment from "moment";

import { PRODUCT_TYPE } from "../../../../constants/policyType";
import PaymentServices from "../../../../services/PaymentServices";
import { formatCoveragesTravel } from "../../../../utils/coverages";
import { deepGet } from "../../../../utils/utility";
import { StatusResponseItem } from "../../../../services/dto/request/PaymentRequests.dto";
import {
  GetRiskDataRequest,
  InsuredTravelDto,
  TravelAddressDto,
} from "../../../../services/TravelServices.dto";
import { AddressData } from "./travel-forms/address-item/address-form-item";
import { TravelInsuredStepData } from "./travel-insured/travel-insured-types";

export const isPaid = async (payment: {
  setLoading: Function;
  loader: boolean;
  pedido: string;
}): Promise<boolean> => {
  try {
    const paymentStatus = (await PaymentServices.getPaymentStatus(payment).then((response) => {
      return response;
    })) as StatusResponseItem[];
    const lastPayment = paymentStatus[paymentStatus.length - 1];
    if (lastPayment.state === "Paid") {
      return true;
    }
    return false;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const getTravelAddressRequestData = ({
  address,
  internalName,
}: {
  address: AddressData;
  internalName: string;
}): TravelAddressDto => {
  return {
    address: address.address && address.address.value,
    postalCode: address.postalCode,
    town: address.town && address.town.value,
    country: (address.country && address.country.code) || "",
    door: address.door || "",
    floor: address.floor || "",
    portal: address.portal || "",
    province:
      internalName === PRODUCT_TYPE.TRAVEL_WELCOME
        ? address.province && address.province.value
        : address.province && address.province.code,
    stairs: address.stairs || "",
    number: address.number,
    wayType: address.wayType,
  };
};

const getTravelInsuredsRequestData = ({
  data,
  internalName,
}: {
  data: TravelInsuredStepData;
  internalName: string;
}): InsuredTravelDto[] => {
  const coverages = formatCoveragesTravel(data.taker.policy.coverages);

  const newInsureds = data.insureds.map((insured) => {
    const foreignAddress = insured.foreignAddress && insured.foreignAddress.address
      ? {
          address: insured.foreignAddress.address || "",
          country: insured.foreignAddress.country || "",
        }
      : undefined;

    return {
      address: getTravelAddressRequestData({ address: insured.address, internalName }),
      birthDate: insured.birthDate || "",
      docType: insured.docType,
      docNumber: insured.docNumber,
      passportDate: insured.passportDate,
      email: insured.email,
      lastname1: insured.lastname1,
      mobilePhone: insured.mobilePhone,
      landlinePhone: insured.landlinePhone,
      name: insured.name,
      coverages,
      gender: insured.gender,
      lastname2: insured.lastname2 ? insured.lastname2 : "",
      nationality:
        insured.nationality && typeof insured.nationality === "string"
          ? insured.nationality
          : "",
      foreignAddress,
      sameAsTaker: insured.sameAsTaker,
    };
  });
  return newInsureds;
};

export const getTravelRiskDataRequest = ({
  data,
  internalName,
}: {
  data: TravelInsuredStepData;
  internalName: string;
}): GetRiskDataRequest => {
  const takerForeignAddress = data.taker.foreignAddress;
  const foreignAddress =
    takerForeignAddress && takerForeignAddress.address
      ? {
          address: takerForeignAddress.address || "",
          country: takerForeignAddress.country ? takerForeignAddress.country.value : "",
        }
      : undefined;

  const requestData: GetRiskDataRequest = {
    idcotiza: data.idcotiza,
    taker: {
      address: getTravelAddressRequestData({ address: data.taker.address, internalName }),
      birthDate: data.taker.physicalPerson ? data.taker.birthDate : moment().format("DD/MM/YYYY"),
      docNumber: data.taker.docNumber,
      docType: data.taker.docType,
      passportDate: data.taker.passportDate,
      email: data.taker.email,
      lastname1: data.taker.lastname1,
      lastname2: data.taker.lastname2 ? data.taker.lastname2 : "",
      nationality:
        data.taker.nationality && typeof data.taker.nationality === "string"
          ? data.taker.nationality
          : "",
      foreignAddress,
      landlinePhone: data.taker.landlinePhone,
      mobilePhone: data.taker.mobilePhone,
      name: data.taker.name,
      gender: data.taker.physicalPerson ? data.taker.gender : "",
      physicalPerson: data.taker.physicalPerson,
      policy: {
        startDate: moment().format("DD/MM/YYYY"),
        selectedMediator: deepGet(data, "taker.policy.selectedMediator", "") || "",
        paymentType: data.taker.policy.paymentType,
      },
    },
    insureds: getTravelInsuredsRequestData({ data, internalName }),
    travelInfo: {
      departureDate: deepGet(data, "taker.policy.departureDate", "") || "",
      returnDate: deepGet(data, "taker.policy.returnDate", "") || "",
      travelDestination: deepGet(data, "taker.policy.travelDestination", "") || "",
      residenceCountry: deepGet(data, "taker.policy.residenceCountry", "") || "",
    },
    productType: internalName,
  };

  return requestData;
};
